<template>
  <teleport to="body">
    <div
      v-if="discovery.inTour && step"
      class="pointer-events-none fixed inset-0 z-[70]"
      role="dialog"
      aria-modal="true"
      :aria-label="step.title"
    >
      <!-- Voile plein écran quand l'étape n'a pas de cible (accueil / conclusion). -->
      <div
        v-if="!rect"
        v-show="!appModalOpen"
        class="pointer-events-auto absolute inset-0 bg-altoneo-900/60"
        :class="reduceMotion ? '' : 'transition-opacity duration-200'"
      />

      <!-- Projecteur : trou sur la cible, le box-shadow assombrit le reste. -->
      <div
        v-else
        v-show="!appModalOpen"
        class="absolute rounded-lg ring-2 ring-sunflower-gold"
        :class="reduceMotion ? '' : 'transition-all duration-200 ease-out'"
        :style="spotlightStyle"
      />

      <!-- Bulle. Masquée pendant qu'une modale de l'app est ouverte, pour ne pas
           la recouvrir ni intercepter ses clics (ex. formulaire « Nouvelle idée »). -->
      <div
        v-show="!appModalOpen"
        ref="bubbleRef"
        tabindex="-1"
        class="pointer-events-auto fixed w-[min(22rem,calc(100vw-2rem))] rounded-xl bg-white p-5 shadow-altoneo-pop outline-none dark:bg-alt-dark-surface dark:text-altoneo-50"
        :class="reduceMotion ? '' : 'transition-all duration-200 ease-out'"
        :style="bubbleStyle"
        @keydown.esc="discovery.closeTour()"
      >
        <div class="mb-2 flex items-center justify-between gap-3">
          <span class="text-xs font-medium uppercase tracking-wide text-slate-400">
            {{ chapter?.title }}
          </span>
          <span class="shrink-0 text-xs tabular-nums text-slate-400">
            {{ discovery.stepIndex + 1 }} / {{ chapter?.steps.length ?? 0 }}
          </span>
        </div>

        <h3 class="mb-1.5 text-base font-semibold text-space-indigo dark:text-altoneo-50">
          {{ step.title }}
        </h3>
        <p class="text-sm leading-relaxed text-slate-600 dark:text-alt-dark-muted">
          {{ step.body }}
        </p>

        <p
          v-if="step.hint"
          class="mt-3 rounded-lg bg-sunflower-gold/10 px-3 py-2 text-xs text-slate-600 dark:text-alt-dark-muted"
        >
          👉 {{ step.hint }}
        </p>

        <!-- Progression du chapitre -->
        <div class="mt-4 h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-alt-dark-raised">
          <div
            class="h-full rounded-full bg-space-indigo transition-[width] duration-200"
            :style="{ width: chapterProgress }"
          />
        </div>

        <div class="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            class="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-altoneo-50"
            @click="discovery.closeTour()"
          >
            Quitter
          </button>
          <div class="flex items-center gap-2">
            <AltButton
              v-if="discovery.stepIndex > 0"
              variant="ghost"
              size="sm"
              @click="discovery.prev()"
            >
              Précédent
            </AltButton>
            <AltButton variant="primary" size="sm" @click="discovery.next()">
              {{ isLast ? 'Terminer' : 'Suivant' }}
            </AltButton>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDiscoveryStore } from '@/stores/discovery';

const discovery = useDiscoveryStore();
const route = useRoute();
const router = useRouter();

// Vrai quand une modale de l'application (AltModal) est ouverte : on masque alors
// la visite pour ne pas la recouvrir.
const appModalOpen = ref(false);

const bubbleRef = ref<HTMLElement | null>(null);
const rect = ref<DOMRect | null>(null);
const viewport = ref({ w: window.innerWidth, h: window.innerHeight });
// Hauteur réelle de la bulle (dépend du contenu de l'étape) : sert à la borner
// entièrement dans l'écran, pour qu'elle ne sorte jamais du viewport.
const bubbleH = ref(0);

function measureBubble(): void {
  if (bubbleRef.value) bubbleH.value = bubbleRef.value.offsetHeight;
}

const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const step = computed(() => discovery.currentStep);
const chapter = computed(() => discovery.currentChapter);
const isLast = computed(
  () => !!chapter.value && discovery.stepIndex >= chapter.value.steps.length - 1,
);
const chapterProgress = computed(() => {
  const total = chapter.value?.steps.length ?? 0;
  if (!total) return '0%';
  return `${Math.round(((discovery.stepIndex + 1) / total) * 100)}%`;
});

const PAD = 6;

/** Style du projecteur : rectangle padé + box-shadow géant pour assombrir. */
const spotlightStyle = computed(() => {
  const r = rect.value;
  if (!r) return {};
  return {
    left: `${r.left - PAD}px`,
    top: `${r.top - PAD}px`,
    width: `${r.width + PAD * 2}px`,
    height: `${r.height + PAD * 2}px`,
    boxShadow: '0 0 0 9999px rgba(15, 15, 40, 0.6)',
  } as Record<string, string>;
});

const MARGIN = 16;

/**
 * Positionne la bulle près de la cible (ou centrée sans cible) en la gardant
 * TOUJOURS entièrement dans l'écran : gauche/droite et haut/bas sont bornés en
 * tenant compte de la largeur et de la hauteur réelle de la bulle. Sans ce
 * bornage vertical, une cible haute (grille, barre d'onglets, tableau) poussait
 * la bulle hors du viewport — contenu et boutons devenaient invisibles.
 */
const bubbleStyle = computed(() => {
  const BW = Math.min(352, viewport.value.w - 2 * MARGIN);
  const BH = bubbleH.value;
  const GAP = 14;
  const r = rect.value;

  const clampLeft = (x: number): number =>
    Math.max(MARGIN, Math.min(x, viewport.value.w - BW - MARGIN));
  const clampTop = (y: number): number =>
    Math.max(MARGIN, Math.min(y, Math.max(MARGIN, viewport.value.h - BH - MARGIN)));

  if (!r) {
    // Centrée (étape d'accueil / conclusion).
    return {
      left: `${Math.round(clampLeft((viewport.value.w - BW) / 2))}px`,
      top: `${Math.round(clampTop((viewport.value.h - BH) / 2))}px`,
      width: `${BW}px`,
    } as Record<string, string>;
  }

  const placement = step.value?.placement ?? 'auto';
  const spaceBelow = viewport.value.h - r.bottom;
  const spaceAbove = r.top;
  const needed = BH + GAP + MARGIN;
  let below: boolean;
  if (placement === 'bottom') below = true;
  else if (placement === 'top') below = false;
  // « auto » : sous la cible si ça tient, sinon du côté le plus large.
  else below = spaceBelow >= needed || spaceBelow >= spaceAbove;

  const top = clampTop(below ? r.bottom + GAP : r.top - GAP - BH);
  const left = clampLeft(r.left + r.width / 2 - BW / 2);
  return { left: `${Math.round(left)}px`, top: `${Math.round(top)}px`, width: `${BW}px` };
});

/**
 * Localise la cible de l'étape courante. Après une navigation de route, l'élément
 * peut n'apparaître qu'au tick suivant : on réessaie brièvement avant d'abandonner
 * (bulle alors centrée, sans projecteur).
 */
function locateTarget(): void {
  const sel = step.value?.target;
  if (!sel) {
    rect.value = null;
    return;
  }
  let tries = 0;
  const attempt = (): void => {
    const el = document.querySelector(sel) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      // Laisse le défilement se poser avant de mesurer.
      window.setTimeout(() => {
        const found = document.querySelector(sel) as HTMLElement | null;
        rect.value = found ? found.getBoundingClientRect() : null;
      }, reduceMotion ? 0 : 180);
      return;
    }
    // Budget généreux (~4 s) : certaines pages ne peignent leur cible qu'après
    // un chargement de données (plan de charge, budget…) suivant la navigation.
    if (tries++ < 26) window.setTimeout(attempt, 150);
    else rect.value = null; // cible absente : bulle centrée.
  };
  attempt();
}

// --- Avance automatique des étapes « à toi de jouer » ---
// Dès que l'action attendue est réalisée (navigation, apparition d'un élément,
// fermeture d'une modale), on passe seul à l'étape suivante.
let advanced = false; // une seule avance auto par étape
let sawModal = false; // une modale s'est ouverte pendant l'étape (pour modalClosed)
let armedRoute: string | null = null; // route de référence (pour routeChange)
let appearBaseline = false; // l'élément « appear » était-il déjà là à l'armement ?

/**
 * Détecte une modale applicative ouverte. AltModal verrouille le scroll du body
 * (`overflow: hidden`) de façon SYNCHRONE à l'ouverture / fermeture, hors de sa
 * transition. C'est un signal fiable même quand l'élément de modale s'attarde
 * dans le DOM (transition de sortie throttlée sur un onglet caché) — bien plus
 * robuste que de chercher l'élément lui-même.
 */
function detectAppModal(): boolean {
  return document.body.style.overflow === 'hidden';
}

function autoAdvance(): void {
  if (advanced) return;
  advanced = true;
  discovery.next();
}

/** Rejoué à chaque mutation du DOM : met à jour l'état modale et déclenche l'avance. */
function scanDom(): void {
  appModalOpen.value = detectAppModal();
  const adv = step.value?.advanceOn;
  if (!adv) return;
  if (adv.type === 'appear') {
    if (!appearBaseline && document.querySelector(adv.selector)) autoAdvance();
  } else if (adv.type === 'modalClosed') {
    if (appModalOpen.value) sawModal = true;
    else if (sawModal) autoAdvance();
  }
}

/** Va sur la route de l'étape si besoin, puis localise la cible et arme l'avance. */
async function applyStep(): Promise<void> {
  const s = step.value;
  if (!s) return;
  // Réinitialise l'armement de l'avance auto pour cette étape.
  advanced = false;
  sawModal = false;
  armedRoute = null;
  if (s.route && route.path !== s.route) {
    await router.push(s.route);
  }
  await nextTick();
  locateTarget();
  await nextTick();
  // Le contenu de l'étape est rendu : on mesure la bulle avant de la placer.
  measureBubble();
  bubbleRef.value?.focus();
  // Arme l'avance auto : route de référence + présence initiale de la cible « appear ».
  armedRoute = route.path;
  appearBaseline =
    s.advanceOn?.type === 'appear' ? !!document.querySelector(s.advanceOn.selector) : false;
  scanDom();
}

function onReflow(): void {
  viewport.value = { w: window.innerWidth, h: window.innerHeight };
  // La largeur de la bulle peut changer (petits écrans) → sa hauteur aussi.
  measureBubble();
  const sel = step.value?.target;
  if (!sel) return;
  const el = document.querySelector(sel) as HTMLElement | null;
  rect.value = el ? el.getBoundingClientRect() : rect.value;
}

// Recalcule à chaque changement d'étape / de chapitre.
watch(
  () => [discovery.chapterId, discovery.stepIndex, discovery.inTour] as const,
  () => {
    if (discovery.inTour && step.value) void applyStep();
    else {
      rect.value = null;
      appModalOpen.value = false;
    }
  },
  { immediate: true },
);

// Avance auto sur navigation (l'utilisateur a ouvert un plan / projet).
watch(
  () => route.path,
  (p) => {
    if (step.value?.advanceOn?.type === 'routeChange' && armedRoute !== null && p !== armedRoute) {
      autoAdvance();
    }
  },
);

// Deux observateurs : (1) le sous-arbre du body pour l'apparition d'éléments
// cibles (ouverture d'un onglet) ; (2) l'attribut `style` du body pour capter
// l'ouverture / fermeture d'une modale via le verrou de scroll — fiable même si
// l'élément de modale s'attarde dans le DOM.
let domObserver: MutationObserver | null = null;
let bodyObserver: MutationObserver | null = null;
onMounted(() => {
  domObserver = new MutationObserver(() => scanDom());
  domObserver.observe(document.body, { childList: true, subtree: true });
  bodyObserver = new MutationObserver(() => scanDom());
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
});

window.addEventListener('scroll', onReflow, true);
window.addEventListener('resize', onReflow);
onBeforeUnmount(() => {
  domObserver?.disconnect();
  bodyObserver?.disconnect();
  window.removeEventListener('scroll', onReflow, true);
  window.removeEventListener('resize', onReflow);
});
</script>
